import { Stack, Typography, useTheme } from '@mui/material';
import { find } from 'lodash';
import { useMemo, useState } from 'react';
import StyledWhiteBox from 'src/components/common/StyledWhiteBox';
import FlagCountry from 'src/components/common/FlagCountry';
import TooltipFormLabel from 'src/components/common/TooltipFormLabel';
import CustomTable from 'src/components/Table';
import { RankByDataPackage, RankByDataPackageValue } from 'src/features/AnalyticsPackage/Common/config';
import { useQueryParams } from 'src/hooks/useQueryParam';
import { useQueryListDataPackage } from 'src/services/queries/useQueryTradeData';
import { TypeDataPackage } from 'src/utils/constants/enum';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import { ITypeTradeDataPackage } from 'src/utils/typings/response';
import { renderTextOrDefault, renderNumberOrDefault } from 'src/utils/helpers/stringHelpers';

const TradeBalanceDataPackage = () => {
  const theme = useTheme();
  const [params, setParams] = useState({
    page: 0,
    page_size: 25,
  });
  const {
    rank_by = RankByDataPackage.NumberBol,
    type_package = TypeDataPackage.top_20_export_company_in_country,
    trade_data_from = '',
    trade_data_to = '',
    import_country = '',
    export_country = '',
  } = useQueryParams();
  const rankByLabel = find(RankByDataPackageValue, (o) => o.value === rank_by);

  const { data, isFetching } = useQueryListDataPackage({
    type_package: String(type_package) as TypeDataPackage,
    trade_data_from: String(trade_data_from),
    trade_data_to: String(trade_data_to),
    import_country: String(import_country),
    export_country: String(export_country),
    rank_by: String(rank_by) as RankByDataPackage,
  });

  const listTradeBalance = getArrayDefault(data?.data);

  const listTradeBalancePaginate = useMemo(() => {
    return listTradeBalance.slice(params.page * params.page_size, (params.page + 1) * params.page_size);
  }, [params, listTradeBalance]);

  const configColumn: ConfigCell<ITypeTradeDataPackage>[] = [
    {
      disablePadding: false,
      id: 'import_country',
      label: 'Import Country',
      cell: () => (
        <Stack spacing={1} direction="row">
          <FlagCountry width={24} code={String(import_country) || ''} />
          <Typography component={'span'} noWrap variant="body2">
            {renderTextOrDefault(String(import_country))}
          </Typography>
        </Stack>
      ),
    },
    {
      disablePadding: false,
      id: 'export_country',
      label: 'Export Country',
      cell: () => (
        <Stack spacing={1} direction="row">
          <FlagCountry width={24} code={String(export_country) || ''} />
          <Typography component={'span'} noWrap variant="body2">
            {renderTextOrDefault(String(export_country))}
          </Typography>
        </Stack>
      ),
    },
    {
      disablePadding: false,
      id: 'hs_code',
      label: 'HS Code',
      width: 300,
      cell: (data: ITypeTradeDataPackage) => (
        <Typography component={'span'} variant="body2" color="text.secondary">
          {renderTextOrDefault(data.hs_code)} - {renderTextOrDefault(data.hscode_description)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'total_imports',
      label: (
        <Stack>
          <Typography variant="inherit">Total Imports</Typography>
          <Typography variant="inherit">({renderTextOrDefault(rankByLabel?.label)})</Typography>
        </Stack>
      ),
      cell: (data: ITypeTradeDataPackage) => (
        <Typography component={'span'} noWrap variant="body2">
          {renderNumberOrDefault(data.total_imports)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'total_exports',
      label: (
        <Stack>
          <Typography variant="inherit">Total Exports</Typography>
          <Typography variant="inherit">({renderTextOrDefault(rankByLabel?.label)})</Typography>
        </Stack>
      ),
      cell: (data: ITypeTradeDataPackage) => (
        <Typography component={'span'} noWrap variant="body2">
          {renderNumberOrDefault(data.total_exports)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'trade_balance',
      label: (
        <Stack>
          <Typography variant="inherit">Trade Balance</Typography>
          <Typography variant="inherit">(Exp- Imp)</Typography>
        </Stack>
      ),
      styledCell: (row: ITypeTradeDataPackage) => styleForEachCell(row),
      cell: (data: ITypeTradeDataPackage) => (
        <Typography component={'span'} noWrap variant="body2">
          {renderNumberOrDefault(data.trade_balance)}
          <TooltipFormLabel title={renderTextTooltip(data)} />
        </Typography>
      ),
    },
  ];

  const renderTextTooltip = (row: ITypeTradeDataPackage) => {
    if (Number(row?.trade_balance) > 0) {
      return `Surplus for the ${import_country}`;
    }
    return `Deficit for the ${import_country}`;
  };

  const styleForEachCell = (row: ITypeTradeDataPackage) => {
    if (Number(row?.trade_balance) > 0) {
      return {
        backgroundColor: '#F0FDF4',
      };
    }
    return {
      backgroundColor: '#FEF2F2',
    };
  };

  return (
    <StyledWhiteBox p={theme.spacing(3)}>
      <Typography variant="h5" mb={3}>
        Trade Balance Data
      </Typography>
      <CustomTable
        page={params.page}
        pageSize={params.page_size}
        onChangePage={(e) => setParams({ ...params, ...{ page: e } })}
        onChangePageSize={(e) => setParams({ ...params, ...{ page: 0, page_size: e } })}
        totalRecords={listTradeBalance.length}
        loading={isFetching}
        data={listTradeBalancePaginate}
        dataKey="hs_code"
        configColumn={configColumn}
      />
    </StyledWhiteBox>
  );
};

export default TradeBalanceDataPackage;
